/**
 * The challenge configurator's data model (ADR-0033): the public catalog grouped the way a buyer chooses — product →
 * evaluation → account size → challenge type → model and reward share — and every figure read from the API.
 *
 * Two kinds of "−/+" (docs/research/upcomers-conditions.md): the reward share and the model are real options of one
 * version (its axes and price matrix); the profit target, drawdowns, reward cycle and trading days are not options —
 * those steppers move to the neighbouring challenge type of the same group that has the next value.
 */
import type { ProgramRules } from "@prop/contracts";

export type Choices = Record<string, string>;
export type PriceEntry = { when: Choices; priceUsd: string; listPriceUsd: string; activationFeeUsd?: string; activationFeeListUsd?: string };
export type Axis = { key: string; default: string; values: Array<{ value: string; changes: string[] }> };
export type Offers = { extraAccounts?: number; freeResets?: number };
/** The one bonus a purchase carries (owner, 2026-10-30): the buyer picks it; the sale price applies either way. */
export type OfferKey = "extraAccounts" | "freeResets";
export const OFFER_KEYS: readonly OfferKey[] = ["extraAccounts", "freeResets"];
/** The bonuses a version offers, in display order. */
export const offerKeys = (o: Offers | null | undefined): OfferKey[] => OFFER_KEYS.filter((k) => !!o?.[k]);
/** The picked bonus if the version offers it, else the first it offers. */
export const pickOffer = (o: Offers | null | undefined, want: string | null | undefined): OfferKey | null => {
  const keys = offerKeys(o);
  return keys.find((k) => k === want) ?? keys[0] ?? null;
};
export type Product = "markets" | "futures" | "perps";
export type Evaluation = "instant" | "1step" | "2step" | "sprint";

export type CatalogProgram = {
  slug: string;
  family: string;
  name: string;
  market: "fx" | "futures" | "crypto";
  billing: string;
  versionId: string;
  version: number;
  rulesHash: string;
  rules: ProgramRules;
  priceUsd: string | null;
  listPriceUsd: string | null;
  launchPricing?: boolean;
  meta?: { product?: Product; evaluation?: Evaluation; type?: string; order?: number };
  evaluation?: Evaluation;
  options?: Axis[];
  prices?: PriceEntry[] | null;
  offers?: Offers | null;
  defaultChoices?: Choices;
  effectiveRules?: ProgramRules;
  /** Instruments its rules admit, counted by the API (public, so guests see it too). */
  instrumentCount?: number;
};

/** A configuration's price: the matrix row of its choices, or the version's single price. */
export type Price = { priceUsd: string | null; listPriceUsd: string | null; activationFeeUsd: string | null; activationFeeListUsd: string | null };

const PRODUCT_OF: Record<CatalogProgram["market"], Product> = { fx: "markets", futures: "futures", crypto: "perps" };
const EVAL_ORDER: Evaluation[] = ["instant", "1step", "2step", "sprint"];

export const sizeOf = (p: CatalogProgram) => Number(p.rules.accountSize);
export const productOf = (p: CatalogProgram): Product => p.meta?.product ?? PRODUCT_OF[p.market];
export function evaluationOf(p: CatalogProgram): Evaluation {
  if (p.meta?.evaluation) return p.meta.evaluation;
  if (p.evaluation) return p.evaluation;
  const n = p.rules.phases.filter((x) => x.kind === "evaluation").length;
  if (n) return n > 1 ? "2step" : "1step";
  return p.rules.phases.some((x) => x.timeWindow) ? "sprint" : "instant";
}
const orderOf = (p: CatalogProgram) => p.meta?.order ?? 0;

/** $5K, $1.5M. */
export const sizeLabel = (n: number) => (n >= 1_000_000 ? `$${n / 1_000_000}M` : n >= 1000 ? `$${n / 1000}K` : `$${n}`);

/** The value an axis takes for a buyer's preferences: the preference when the version offers it, else the default. */
export function resolveChoices(p: CatalogProgram, prefs: Choices): Choices {
  const out: Choices = {};
  for (const a of p.options ?? []) out[a.key] = a.values.some((v) => v.value === prefs[a.key]) ? (prefs[a.key] as string) : a.default;
  return out;
}

/** Exactly one matrix row matches (ADR-0033); a version without options has its single price point. */
export function priceOf(p: CatalogProgram, choices: Choices): Price {
  if (!p.prices?.length) return { priceUsd: p.priceUsd, listPriceUsd: p.listPriceUsd, activationFeeUsd: null, activationFeeListUsd: null };
  const rows = p.prices.filter((r) => Object.entries(r.when).every(([k, v]) => choices[k] === v));
  const r = rows.length === 1 ? rows[0] : undefined;
  if (!r) return { priceUsd: null, listPriceUsd: null, activationFeeUsd: null, activationFeeListUsd: null };
  return { priceUsd: r.priceUsd, listPriceUsd: r.listPriceUsd, activationFeeUsd: r.activationFeeUsd ?? null, activationFeeListUsd: r.activationFeeListUsd ?? null };
}

export const axisOf = (p: CatalogProgram | undefined, key: string) => p?.options?.find((a) => a.key === key);
export const rulesOf = (p: CatalogProgram) => p.effectiveRules ?? p.rules;

// ── Grouping ────────────────────────────────────────────────────────────────────
export type Family = { family: string; name: string; order: number; sizes: CatalogProgram[] };

export function products(all: CatalogProgram[]): Product[] {
  const seen = new Map<Product, number>();
  for (const p of all) seen.set(productOf(p), Math.min(seen.get(productOf(p)) ?? Number.POSITIVE_INFINITY, orderOf(p)));
  return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([k]) => k);
}

export function evaluations(all: CatalogProgram[], product: Product): Evaluation[] {
  const set = new Set(all.filter((p) => productOf(p) === product).map(evaluationOf));
  return EVAL_ORDER.filter((e) => set.has(e));
}

/** Words shared by every family name of a market ("FX ", "Crypto Perps "): the type's own name is the rest. */
function marketPrefix(names: string[]): string {
  const words = names.map((n) => n.split(" "));
  const first = words[0] ?? [];
  let k = 0;
  while (k < first.length - 1 && words.every((w) => w[k] === first[k] && w.length > k + 1)) k++;
  return first.slice(0, k).join(" ");
}

/** Families of one product and evaluation, in the catalog's order, each with its sizes ascending. */
export function familiesOf(all: CatalogProgram[], product: Product, evaluation: Evaluation): Family[] {
  const inProduct = all.filter((p) => productOf(p) === product);
  const base = (p: CatalogProgram) => p.name.replace(/\s[\d.]+[KM]$/, "");
  const prefix = marketPrefix([...new Set(inProduct.map(base))]);
  const byFamily = new Map<string, CatalogProgram[]>();
  for (const p of inProduct.filter((x) => evaluationOf(x) === evaluation)) byFamily.set(p.family, [...(byFamily.get(p.family) ?? []), p]);
  return [...byFamily.entries()]
    .map(([family, sizes]) => {
      const first = sizes[0] as CatalogProgram;
      const name = base(first).slice(prefix.length).trim() || base(first);
      return { family, name, order: orderOf(first), sizes: [...sizes].sort((a, b) => sizeOf(a) - sizeOf(b)) };
    })
    .sort((a, b) => a.order - b.order);
}

/** The template of a family nearest to a size (the same size when it exists). */
export function nearest(f: Family, size: number): CatalogProgram {
  return f.sizes.reduce((best, p) => (Math.abs(sizeOf(p) - size) < Math.abs(sizeOf(best) - size) ? p : best), f.sizes[0] as CatalogProgram);
}

// ── Metrics behind the type-switching steppers ─────────────────────────────────
export type Metric = "target" | "daily" | "max" | "cycle" | "days";
const CYCLE_RANK = { on_demand: 0, weekly: 1, bi_weekly: 2, monthly: 3 } as const;
const pctOf = (a: { pct: string } | { usd: string } | undefined, size: number): number | null => (!a ? null : "pct" in a ? Number(a.pct) : (Number(a.usd) / size) * 100);

/** The value a stepper compares, from the type's default rules at that size; null = the type has no such rule. */
export function metricOf(p: CatalogProgram, m: Metric): number | null {
  const r = rulesOf(p);
  const ph = r.phases[0];
  const size = sizeOf(p);
  if (!ph) return null;
  if (m === "target") return pctOf(ph.profitTarget, size);
  if (m === "daily") return pctOf(ph.dailyLoss?.amount, size) ?? 0;
  if (m === "max") return pctOf(ph.maxLoss.amount, size);
  if (m === "cycle") return r.payouts ? CYCLE_RANK[r.payouts.cycle] : null;
  return r.payouts ? (r.payouts.profitableDays?.days ?? 0) : null;
}

const METRICS: Metric[] = ["target", "daily", "max", "cycle", "days"];

/**
 * The neighbouring type for a "−" (dir −1) or "+" (dir 1) on a rule: among the group's types, the next distinct value
 * of that rule; when several types share it, the one most like the current type. null = nothing further that way.
 */
export function neighbour(group: Family[], current: Family, size: number, m: Metric, dir: -1 | 1): Family | null {
  const here = metricOf(nearest(current, size), m);
  if (here === null) return null;
  const vals = group
    .filter((f) => f.family !== current.family)
    .map((f) => ({ f, v: metricOf(nearest(f, size), m) }))
    .filter((x): x is { f: Family; v: number } => x.v !== null && (dir > 0 ? x.v > here : x.v < here));
  if (!vals.length) return null;
  const target = dir > 0 ? Math.min(...vals.map((x) => x.v)) : Math.max(...vals.map((x) => x.v));
  const same = vals.filter((x) => x.v === target);
  const likeness = (f: Family) => METRICS.filter((k) => k !== m && metricOf(nearest(f, size), k) === metricOf(nearest(current, size), k)).length;
  same.sort((a, b) => likeness(b.f) - likeness(a.f) || a.f.order - b.f.order);
  return same[0]?.f ?? null;
}

// ── Taglines: what sets a type apart in its group, read from the rules ─────────────
export type Tagline = "noDaily" | "lowestTarget" | "noConsistency" | "cheapest" | "widest" | "fullSplit" | "turbo" | "window";

function features(p: CatalogProgram, prefs: Choices) {
  const r = rulesOf(p);
  const ph = r.phases[0];
  const size = sizeOf(p);
  const splits = axisOf(p, "split")?.values.map((v) => Number(v.value)) ?? [Number(r.payouts?.profitSplitPct ?? 0)];
  return {
    noDaily: !r.phases.some((x) => x.dailyLoss),
    target: pctOf(ph?.profitTarget, size),
    noConsistency: !r.payouts?.consistencyPct && !r.phases.some((x) => x.consistency),
    price: Number(priceOf(p, resolveChoices(p, prefs)).priceUsd ?? Number.POSITIVE_INFINITY),
    maxLoss: pctOf(ph?.maxLoss.amount, size) ?? 0,
    split: Math.max(...splits),
    turbo: !!axisOf(p, "model"),
    windowMin: r.phases.find((x) => x.timeWindow)?.timeWindow?.minutes ?? null,
  };
}

/** The first distinction the type alone has in its group (unique best), in a fixed order; null when none. */
export function taglineOf(group: Family[], f: Family, size: number, prefs: Choices): { key: Tagline; hours?: number } | null {
  const all = group.map((g) => ({ g, x: features(nearest(g, size), prefs) }));
  const me = all.find((a) => a.g.family === f.family)?.x;
  if (!me) return null;
  if (me.windowMin) return { key: "window", hours: me.windowMin / 60 };
  if (all.length < 2) return null;
  const others = all.filter((a) => a.g.family !== f.family).map((a) => a.x);
  const unique = (ok: boolean, beats: (o: (typeof others)[number]) => boolean) => ok && others.every(beats);
  if (unique(me.noDaily, (o) => !o.noDaily)) return { key: "noDaily" };
  if (unique(me.target !== null, (o) => o.target === null || (me.target as number) < o.target)) return { key: "lowestTarget" };
  if (unique(me.noConsistency, (o) => !o.noConsistency)) return { key: "noConsistency" };
  if (unique(Number.isFinite(me.price), (o) => me.price < o.price)) return { key: "cheapest" };
  if (unique(true, (o) => me.maxLoss > o.maxLoss)) return { key: "widest" };
  if (unique(true, (o) => me.split > o.split)) return { key: "fullSplit" };
  if (unique(me.turbo, (o) => !o.turbo)) return { key: "turbo" };
  return null;
}

/** Whole percent off the list price (90 for $139 / $1,390); null when there is no discount. */
export function percentOff(price: string | null, list: string | null): number | null {
  const p = Number(price);
  const l = Number(list);
  if (!price || !list || !(l > p) || !(l > 0)) return null;
  return Math.round((1 - p / l) * 100);
}

/** list − price in cents, back to a decimal string, without a float in between. */
export function saving(price: string | null, list: string | null): string | null {
  if (!price || !list) return null;
  const c = (s: string) => {
    const [w = "0", f = ""] = s.split(".");
    return BigInt(w) * 100n + BigInt(`${f}00`.slice(0, 2));
  };
  const d = c(list) - c(price);
  return d > 0n ? `${d / 100n}.${String(d % 100n).padStart(2, "0")}` : null;
}

/** The query string of a configuration for the buy page: the template and its non-default choices are all it needs. */
export function buyHref(p: CatalogProgram, choices: Choices, offer?: OfferKey | null): string {
  const q = new URLSearchParams({ program: p.slug });
  for (const [k, v] of Object.entries(choices)) q.set(`opt.${k}`, v);
  if (offer) q.set("offer", offer);
  return `/buy?${q.toString()}`;
}

/** Choices read back from a buy URL (`opt.split=100&opt.model=turbo`). */
export function choicesFromSearch(search: URLSearchParams): Choices {
  const out: Choices = {};
  for (const [k, v] of search.entries()) if (k.startsWith("opt.") && /^[a-z0-9_]{1,32}$/.test(v)) out[k.slice(4)] = v;
  return out;
}
